import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import {
  FluentProvider,
  MessageBar,
  MessageBarBody,
  webLightTheme,
} from '@fluentui/react-components';
import { Controller, UseFormMethods } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import TerminationLifecycleStateDropdown from '../../common/formFields/TerminationLifecycleStateDropdown';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import {
  IBlockerReviewApproval,
  IEntityTerminationActionItem,
  TerminationLifecycleStatus,
} from '../types';
import { GET_TERMINATION_LIFECYCLE_STATUSES_QUERY } from '../../../utils/statApi/EntityStatusApi';

interface IBlockerReviewApprovalFormProps {
  form: UseFormMethods<IBlockerReviewApproval>;
  data: IBlockerReviewApproval;
  blockerItems: IEntityTerminationActionItem[];
}

const BlockerReviewApprovalForm = ({
  form,
  data,
  blockerItems,
}: IBlockerReviewApprovalFormProps) => {
  const { control, errors, setValue } = form;

  const onTerminationLifecycleStatusChange = (value: IDropdownOption): void => {
    setValue('terminationLifecycleStatus', value, { shouldValidate: true });
  };

  const {
    loading,
    data: terminationLifecycleData,
    error: lifecycleError,
  } = useQuery(GET_TERMINATION_LIFECYCLE_STATUSES_QUERY);

  const hasIncompleteBlockers = blockerItems.some((item) => !item.dateCompleted);

  const allowedStatuses = [
    TerminationLifecycleStatus.ELIGIBLE,
    TerminationLifecycleStatus.IN_PROCESS,
  ];

  const dropdownOptionsData =
    !loading && terminationLifecycleData
      ? (terminationLifecycleData.terminationLifecycleStatuses as IDropdownReference[]).filter(
          (status) => allowedStatuses.includes(status.fieldName as TerminationLifecycleStatus),
        )
      : [];

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <LoadingErrorMessage loading={loading} error={lifecycleError} />
        {hasIncompleteBlockers && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              You cannot approve this blocker review until all blockers are complete.
            </MessageBarBody>
          </MessageBar>
        )}
        <Controller
          name="terminationLifecycleStatus"
          label="Termination Lifecycle Status"
          valueKey="terminationLifecycleStatus"
          control={control}
          defaultValue={null}
          render={(props: { value: IDropdownReference }) => {
            return (
              <TerminationLifecycleStateDropdown
                label="Termination Lifecycle Status"
                valueKey="terminationLifecycleStatus"
                required
                errors={errors}
                handleChange={onTerminationLifecycleStatusChange}
                value={props.value}
                dropdownOptionsData={dropdownOptionsData}
              />
            );
          }}
          rules={{
            validate: (value?: IDropdownReference) =>
              value?.fieldName?.trim()
                ? undefined
                : 'Please select a Termination Lifecycle Status.',
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default BlockerReviewApprovalForm;
